/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { FormArray } from '@angular/forms';
import { LanguageService } from '../../utilities/language.service';

@Injectable()
export class ValidationService {
    private zIndex: number = 40;

    constructor(private languageService: LanguageService) {
    }

    /**
     * Validate an array for fields to include at least one valid input.
     * @param formArray
     */
    public static atLeastOneRequired(formArray: FormArray): any {
        if (formArray) {
            const invalid = !formArray.controls.some(item => {
                return typeof item?.value === 'boolean' ? item?.value : item?.value?.length;
            });

            return invalid ? { atLeastOne: true } : null;
        }
    }

    /**
     * Error messages
     *
     * @param validatorName
     * @param validatorValue
     * @returns {any}
     */
    public getValidatorErrorMessage(validatorName: string, validatorValue?: any): string {
        const config = {
            required: 'Required',
            invalidEmailAddress: 'invalid_email_address',
            invalidEmailAddressLength: 'invalid_email_address_length',
            invalidPhoneNumber: 'invalid_phone_number',
            invalidIpDomain: 'invalid_ip_domain',
            invalidIp: 'invalid_ip',
            invalidIPV4: 'invalid_ipv4',
            invalidIPV6: 'invalid_ipv6',
            CIDRMissing: 'cidr_missing',
            invalidCIDR: 'invalid_cidr',
            invalidSAN: 'invalid_san',
            'Invalid IP/CIDR': 'invalid_ip_cidr',
            atLeastOne: 'at_least_one_required'
        };

        if (validatorValue) {
            config['minlength'] = this.languageService.translate('minimum_length') + validatorValue.requiredLength;
            config['maxlength'] = this.languageService.translate('maximum_length') + validatorValue.requiredLength;
            config['min'] = this.languageService.translate('value_greater_than') + validatorValue.min;
            config['max'] = this.languageService.translate('value_less_than') + validatorValue.max;
        }

        return config[validatorName];
    }

    /**
     * Validate subnet CIDR
     * Validate the subnet mask with / notation
     * @param {number} subnet
     * @param {number} ipVersion
     * @returns {boolean}
     */
    public static subnetCIDRValidator(subnet: number, ipVersion: number): boolean {
        let min = 0;
        let max = 0;

        switch (ipVersion) {
            case 4:
                min = 0;
                max = 32;
                break;
            case 6:
                min = 0;
                max = 128;
                break;
            default:
                throw new RangeError('Unrecognised IP version should be 4 or 6');
        }

        return (subnet >= min && subnet <= max);
    }

    /**
     * Validate ip/subnet address
     * Split the ip and subnet mask then validate the parts.
     * @param control
     * @returns {any}
     */
    public static formIPCIDRSubnetValidator(control: any): any {
        if (!control || !control.value || control.value === '') {
            return null;
        }

        const splitAddress = control.value.split('/', 3);

        if (splitAddress.length === 1) {
            return { CIDRMissing: true };
        }
        if (!splitAddress[0]) {
            return null;
        }
        if (ValidationService.ipV4Check(splitAddress[0])) {
            if (ValidationService.subnetCIDRValidator(splitAddress[1], 4)) {
                return null;
            } else {
                return { invalidCIDR: true };
            }
        } else if (ValidationService.ipV6Check(splitAddress[0])) {
            if (ValidationService.subnetCIDRValidator(splitAddress[1], 6)) {
                return null;
            } else {
                return { invalidCIDR: true };
            }
        } else {
            return { 'Invalid IP/CIDR': true };
        }
    }

    /**
     * email validator
     * @param control
     * @returns {any}
     */
    public static emailValidator(control: any): any {
        if (!control || !control.value || control.value === '') {
            return null;
        }

        const value = control.value.toLowerCase();
        // RFC 2822 compliant regex
        // eslint-disable-next-line
        let email = value.match(/[a-z\d!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z\d!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z\d](?:[a-z\d-]*[a-z\d])?\.)+[a-z\d](?:[a-z\d-]*[a-z\d])?/);
        if (email && email[0] === control.value.toLowerCase() && email[0].length <= 254) {
            return null;
        } else {
            return email[0]?.length > 254 ? { invalidEmailAddressLength: true } : { invalidEmailAddress: true };
        }
    }

    /**
     * Phone number validator.
     * @param control
     * @returns {any}
     */
    public static phoneNumberValidator(control: any): any {
        if (!control || !control.value || control.value === '') {
            return null;
        }
        // starts with a number 1-9 and then 4-14 digits after that.
        const phoneNumber = control?.value?.toString().match(/^[1-9]\d{3,14}$/);

        return (phoneNumber && phoneNumber[0] === control?.value?.toString()) ? null : { invalidPhoneNumber: true };
    }

    /**
     * ip and domain validator.
     * @param control
     * @returns {any}
     */
    public static ipDomainValidator(control: any): any {
        return (
            !control?.value ||
            ValidationService.ipV4Check(control?.value) ||
            ValidationService.ipV6Check(control?.value) ||
            ValidationService.domainNameCheck(control?.value)
        ) ? null : { invalidIpDomain: true };
    }

    /**
     * Check if an IP v4
     *
     * @param value
     * @returns {boolean}
     */
    public static ipV4Check(value: any): boolean {
        if (value.indexOf('.') !== -1) {
            const ipParts: string[] = value.split('.');
            if (ipParts.length === 4) {
                const regex = new RegExp('[a-z ]', 'g');
                if (ipParts.some(ip => regex.test(ip))) {
                    return false;
                }

                return !ipParts.some(ipPart => ValidationService.ipPartTest(ipPart));
            }

            return false;
        }
    }

    /**
     * Helper function for the 'some' logic.
     * @param ipPart
     * @private
     */
    private static ipPartTest(ipPart: string): boolean {
        return ipPart.length === 0 ||
            (ipPart.length > 1 && ipPart.startsWith('0')) ||
            ipPart.startsWith('-') ||
            parseInt(ipPart, 10) < 0 ||
            parseInt(ipPart, 10) > 255;
    }

    /**
     * Check ipv6 addresses
     *
     * @param ipAddress
     * @returns {boolean}
     */
    public static ipV6Check(ipAddress: string): boolean | string[] {
        // Literal IPv6 addresses may be specified enclosed in square brackets, [...]
        // Strip any brackets...
        ipAddress = ValidationService.trim(ipAddress, '[]');

        // All zero address...
        if (ipAddress.length < 3) {
            return ipAddress === '::';
        }
        // IPv4 address embedded in IPv6...
        if (ValidationService.strpos(ipAddress, '.')) {
            const lastColon = ValidationService.strrpos(ipAddress, ':');
            if (!(lastColon && ValidationService.ipV4Check(
                    ValidationService.substr(ipAddress,
                        <number>lastColon + 1))
            )) {
                return false;
            }

            ipAddress = ValidationService.substr(ipAddress, 0, <number>lastColon) + ':0:0';
        }

        // No zero compression present...
        if (ValidationService.strpos(ipAddress, '::') === false) {
            return ipAddress.match(/(?:[a-f\d]{1,4}:){7}[a-f\d]{1,4}/i);
        }

        // IPv6 address with zero compression...
        const colonCount = ValidationService.substrCount(ipAddress, ':');
        if (colonCount < 8) {
            return ipAddress.match(/(?::|(?:[a-f\d]{1,4}:)+):(?:(?:[a-f\d]{1,4}:)*[a-f\d]{1,4})?/i);
        }

        // Special case with ending or starting double colon...
        if (colonCount === 8) {
            return ipAddress.match(/(?:::)?(?:[a-f\d]{1,4}:){6}[a-f\d]{1,4}(?:::)?/i);
        }

        return false;
    }

    /**
     * Checks what the IP type is.
     * @param control
     * @returns {any}
     */
    public static formIPV4Check(control: any): any {
        return (!control?.value || ValidationService.ipV4Check(control?.value)) ? null : { invalidIPV4: true };
    }

    /**
     * Checks what the IP type is.
     * @param formObject
     */
    public static formIPV6Check(formObject: any): any {
        if (!formObject || !formObject.value || formObject.value === '') {
            return null;
        }
        if (ValidationService.ipV6Check(formObject.value)) {
            return null;
        }

        return { invalidIPV6: true };
    }

    /**
     * Trim characters from the start and end of string.
     * @param value
     * @param charlist
     */
    public static trim(value: string, charlist?: string): string {
        //  discuss at: http://locutus.io/php/trim/
        // original by: Kevin van Donnell (http://kvz.io)
        // improved by: mdsjack (http://www.mdsjack.bo.it)
        // improved by: Alexander Ermolaev (http://snippets.dzone.com/user/AlexanderErmolaev)
        // improved by: Kevin van Zonneveld (http://kvz.io)
        // improved by: Steven Levithan (http://blog.stevenlevithan.com)
        // improved by: Jack
        //    input by: Erkekjetter
        //    input by: DxGx
        // bugfixed by: Onno Marsman (https://twitter.com/onnomarsman)
        //   example 1: trim('    Kevin van Zonneveld    ')
        //   returns 1: 'Kevin van Zonneveld'
        //   example 2: trim('Hello World', 'Hdle')
        //   returns 2: 'o Wor'
        //   example 3: trim(16, 1)
        //   returns 3: '6'
        let whitespace = [
            ' ',
            '\n',
            '\r',
            '\t',
            '\f',
            '\x0b',
            '\xa0',
            '\u2000',
            '\u2001',
            '\u2002',
            '\u2003',
            '\u2004',
            '\u2005',
            '\u2006',
            '\u2007',
            '\u2008',
            '\u2009',
            '\u200a',
            '\u200b',
            '\u2028',
            '\u2029',
            '\u3000'
        ].join('');
        let l: number;
        let i: number;
        value += '';
        if (charlist) {
            whitespace = (charlist + '').replace(/([[\]().?/*{}+$^:])/g, '$1');
        }
        l = value.length;
        for (i = 0; i < l; i++) {
            if (whitespace.indexOf(value.charAt(i)) === -1) {
                value = value.substring(i);
                break;
            }
        }
        l = value.length;
        for (i = l - 1; i >= 0; i--) {
            if (whitespace.indexOf(value.charAt(i)) === -1) {
                value = value.substring(0, i + 1);
                break;
            }
        }

        return whitespace.indexOf(value.charAt(0)) === -1 ? value : '';
    }

    /**
     * strrpos
     *
     * @param haystack
     * @param needle
     * @param offset
     * @returns {number|boolean}
     */
    public static strrpos(haystack: string, needle: string, offset?: number | boolean): number | boolean {
        //  discuss at: http://locutus.io/php/strrpos/
        // original by: Kevin van Zonneveld (http://kvz.io)
        // bugfixed by: Onno Marsman (https://twitter.com/onnomarsman)
        // bugfixed by: Brett Zamir (http://brett-zamir.me)
        let i: number;
        if (offset) {
            i = (haystack + '')
                .slice(<number>offset)
                .lastIndexOf(needle); // strrpos' offset indicates starting point of range till end,
            // while lastIndexOf 's optional 2nd argument indicates ending point of range from the beginning
            if (i !== -1) {
                i += <number>offset;
            }
        } else {
            i = (haystack + '')
                .lastIndexOf(needle);
        }

        return i >= 0 ? i : false;
    }

    /**
     * strpos
     *
     * @param haystack
     * @param needle
     * @param offset
     * @returns {boolean|number}
     */
    public static strpos(haystack: string, needle: string, offset?: number): boolean | number {
        const i = (haystack + '').indexOf(needle, (offset || 0));

        return i === -1 ? false : i;
    }

    /**
     * substr
     *
     * @param str
     * @param start
     * @param len
     * @returns {string|boolean}
     */
    public static substr(str: string, start: number, len?: number): string | boolean {
        //  discuss at: http://locutus.io/php/substr/
        // original by: Martijn Wieringa
        str += '';
        let end = str.length;
        // assumes there are no non-BMP characters;
        // if there may be such characters, then it is best to turn it on (critical in true XHTML/XML)
        if (start < 0) {
            start += end;
        }
        if (typeof len !== 'undefined') {
            if (len < 0) {
                end = len + end;
            } else {
                end = len + start;
            }
        }
        // PHP returns false if start does not fall within the string.
        // PHP returns false if the calculated end comes before the calculated start.
        // PHP returns an empty string if start and end are the same.
        // Otherwise, PHP returns the portion of the string from start to end.
        if (start >= str.length || start < 0 || start > end) {
            return false;
        }

        return str.slice(start, end);
    }

    /**
     * substr_count()
     *
     * @param haystack
     * @param needle
     * @param offset
     * @param length
     * @returns {any}
     */
    public static substrCount(haystack: string, needle: string, offset?: number, length?: number): any {
        // eslint-disable-line camelcase
        //  discuss at: http://locutus.io/php/substr_count/
        // original by: Kevin van Zonneveld (http://kvz.io)
        // bugfixed by: Onno Marsman (https://twitter.com/onnomarsman)
        // improved by: Brett Zamir (http://brett-zamir.me)
        // improved by: Thomas
        let cnt = 0;

        haystack += '';
        needle += '';
        if (isNaN(offset)) {
            offset = 0;
        }
        if (isNaN(length)) {
            length = 0;
        }
        if (needle.length === 0) {
            return false;
        }
        offset--;

        offset = haystack.indexOf(needle, offset + 1);
        while (offset !== -1) {
            if (length > 0 && (offset + needle.length) > length) {
                return false;
            }
            offset = haystack.indexOf(needle, offset + 1);
            cnt++;
        }

        return cnt;
    }

    /**
     * Check if a string is JSON.
     * @param json JSON
     * @returns {any}
     */
    public isJson(json: any): any {
        try {
            json = JSON.parse(json);
        } catch (error) {
            return {
                json: null,
                error
            };
        }

        if (typeof json === 'object' && json !== null) {
            return {
                json,
                error: null
            };
        }

        return {
            json: null,
            error: 'The JSON didn\'t error but it was either not an object or it returned null'
        };
    }

    /**
     * Get the z-index from the class variable, this ensures that the top validation message is on top!
     */
    public getZIndex(): number {
        if (this.zIndex !== 30) {
            return this.zIndex--;
        } else {
            return this.zIndex;
        }
    }

    /**
     * FQDN check.
     * @param {string} name
     * @returns {boolean}
     */
    public static fqdnCheck(name: string): boolean {
        return /(?=^.{4,253}$)(^((?!-)[a-zA-Z0-9-]{0,62}[a-zA-Z0-9]\.)+[a-zA-Z]{2,63}$)/g.test(name);
    }

    /**
     * Host name check.
     * @param {string} hostName
     * @returns {boolean}
     */
    public static hostNameCheck(hostName: string): boolean {
        return /^(?![-\s])(?!^\d+$)[-A-Za-z0-9]{1,63}(?<!-)$/.test(hostName);
    }

    /**
     * Domain name check.
     * @param {string} domainName
     * @returns {boolean}
     */
    public static domainNameCheck(domainName: string): boolean {
        if (domainName === 'localhost') {
            return true;
        }

        const regex: RegExp = new RegExp(/^(https?:\/\/)?(?!-)[A-Za-z0-9-]{1,63}?(?<!-)([\-.][a-z0-9]+)*\.[A-Za-z]{2,6}$/i);

        return regex.test(domainName) && (domainName.split('.').length - 1) <= 127;
    }

    /**
     * Helper function to find key combinations.
     * @param {KeyboardEvent} event
     * @returns {boolean}
     */
    public static actionKeyCombinations(event: KeyboardEvent): boolean {
        return (event.key === 'a' && event.ctrlKey === true) || // Allow: Ctrl+A
            (event.key === 'c' && event.ctrlKey === true) || // Allow: Ctrl+C
            (event.key === 'v' && event.ctrlKey === true) || // Allow: Ctrl+V
            (event.key === 'x' && event.ctrlKey === true) || // Allow: Ctrl+X
            (event.key === 'a' && event.metaKey === true) || // Allow: Cmd+A (Mac)
            (event.key === 'c' && event.metaKey === true) || // Allow: Cmd+C (Mac)
            (event.key === 'v' && event.metaKey === true) || // Allow: Cmd+V (Mac)
            (event.key === 'x' && event.metaKey === true); // Allow: Cmd+X (Mac)
    }
}
